import { useState } from "react"
import { Tooltip } from "antd"
import { WarningOrangeIcon } from "components/ui/icons/Warning/WarningOrangeIcon"
import {
	defaultTooltipStyles,
	getShadowTooltipContainer,
} from "components/ui/customWebviewer/shared/shadowTooltip"
import { SelectModelNumberModal } from "./SelectModelNumberModal/SelectModelNumberModal"
import type { TFurniture, TSelectedModelNumber } from "types/elevationReferences.type"
import type { TModelNumber } from "types/modelNumbers.type"
import style from "./ModelNumberSection.module.scss"

const { section, headerRow, titleGroup, title, requiredMark, editLink, tag, tagRow, warningIcon, selectLink } = style

type Props = {
	furniture: TFurniture
	selectedModelNumber: TSelectedModelNumber | null
	seriesOverrideId: number | null
	missingPriceSeriesName: string | null
	onSelect: (model: TSelectedModelNumber) => Promise<void>
	isOther?: boolean
	isRequired?: boolean
}

const toSelected = (model: TModelNumber): TSelectedModelNumber => ({
	id: model.id,
	model_number: model.model_number,
	image_from_stevens_catalogue: model.image_from_stevens_catalogue,
	dimensions: {
		width: model.dimensions.width,
		height: model.dimensions.height,
		depth: model.dimensions.depth,
		min_size: Number(model.dimensions.min_size ?? 0),
		max_size: Number(model.dimensions.max_size ?? 0),
		avg_size: Number(model.dimensions.avg_size ?? 0),
	},
})

export const ModelNumberSection = ({
	furniture,
	selectedModelNumber,
	seriesOverrideId,
	missingPriceSeriesName,
	onSelect,
	isOther = false,
	isRequired = true,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const modelNumber = selectedModelNumber?.model_number ?? null

	const handleSave = async (model: TModelNumber) => {
		await onSelect(toSelected(model))
	}

	return (
		<div className={section}>
			<div className={headerRow}>
				<div className={titleGroup}>
					<span className={title}>Model number</span>
					{isRequired ? <span className={requiredMark}>*</span> : null}
				</div>
				{modelNumber ? (
					<button type="button" className={editLink} onClick={() => setIsOpen(true)}>
						Edit
					</button>
				) : null}
			</div>
			{modelNumber ? (
				<div className={tagRow}>
					<span className={tag}>{modelNumber}</span>
					{missingPriceSeriesName ? (
						<Tooltip
							title={`No price for series ${missingPriceSeriesName}`}
							placement="top"
							getPopupContainer={getShadowTooltipContainer}
							styles={defaultTooltipStyles}
						>
							<span className={warningIcon}>
								<WarningOrangeIcon />
							</span>
						</Tooltip>
					) : null}
				</div>
			) : (
				<button type="button" className={selectLink} onClick={() => setIsOpen(true)}>
					Select model number
				</button>
			)}
			<SelectModelNumberModal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				furniture={furniture}
				seriesOverrideId={seriesOverrideId}
				initialSelectedId={selectedModelNumber?.id ?? null}
				onSave={handleSave}
				isOther={isOther}
			/>
		</div>
	)
}
